/**
 * Inline Pfand (deposit) picker for the Catalog + Stock right panes.
 *
 * Two dropdowns — primary deposit and crate deposit — populated from
 * ``GET /api/pfand-values`` and persisted via
 * ``PATCH /articles/{chain_root_id}/pfand``. Either field may be
 * cleared independently; the underlying article row stores them as
 * nullable FKs. Refill lines record the deposit value at the moment
 * the refill is written, so changing the FK here only affects future
 * refills — existing refill rows keep the value they were recorded
 * with.
 *
 * Extracted from ``CatalogRightPane`` so that the Stock Overview right
 * pane (spec §5.7) can reuse the exact same widget rather than ship a
 * parallel placeholder.
 */

import { useEffect, useState } from "react";
import { Alert, Select, Space, Typography } from "antd";

import { ApiError, api, type PfandValueRow } from "../../api/client";
import { formatCents } from "../../utils/dateFormat";

const { Text } = Typography;

interface Props {
  chainRootId: number;
  pfandValueId: number | null;
  cratePfandValueId: number | null;
  onSaved: () => void;
}

export function PfandEditor({
  chainRootId,
  pfandValueId,
  cratePfandValueId,
  onSaved,
}: Props) {
  const [options, setOptions] = useState<PfandValueRow[] | null>(null);
  const [savingField, setSavingField] = useState<"primary" | "crate" | null>(
    null,
  );
  const [err, setErr] = useState<string | null>(null);

  // Fetch the lookup table once per mount. The list is tiny (typically
  // the 5 seeded German rates plus maybe one or two operator-added rows)
  // so a single GET is cheap and we don't bother with a global cache.
  // Archived rows are included so an article already-pointing at an
  // archived value still resolves to a label rather than a blank.
  useEffect(() => {
    let cancelled = false;
    void api
      .listPfandValues(true)
      .then((rows) => {
        if (!cancelled) setOptions(rows);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (
    field: "primary" | "crate",
    next: number | null,
  ): Promise<void> => {
    setErr(null);
    setSavingField(field);
    try {
      await api.assignArticlePfand(chainRootId, {
        pfand_value_id: field === "primary" ? next : pfandValueId,
        crate_pfand_value_id: field === "crate" ? next : cratePfandValueId,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSavingField(null);
    }
  };

  const buildOptions = () =>
    (options ?? []).map((p) => ({
      value: p.id,
      label: `${p.label} (${formatCents(p.cents)})${p.active ? "" : " (archived)"}`,
    }));

  return (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Text type="secondary" style={{ minWidth: 90 }}>
          Primary
        </Text>
        <Select<number | null>
          value={pfandValueId}
          placeholder="No deposit"
          style={{ flex: 1, maxWidth: 280 }}
          allowClear
          loading={options === null || savingField === "primary"}
          disabled={options === null}
          options={buildOptions()}
          onChange={(v) => void save("primary", (v as number | null) ?? null)}
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Text type="secondary" style={{ minWidth: 90 }}>
          Crate
        </Text>
        <Select<number | null>
          value={cratePfandValueId}
          placeholder="No crate deposit"
          style={{ flex: 1, maxWidth: 280 }}
          allowClear
          loading={options === null || savingField === "crate"}
          disabled={options === null}
          options={buildOptions()}
          onChange={(v) => void save("crate", (v as number | null) ?? null)}
        />
      </div>
      <Text type="secondary" style={{ fontSize: 11 }}>
        Used on future refill lines. Existing refills keep the value they
        were recorded with.
      </Text>
      {err && <Alert type="error" message={err} showIcon />}
    </Space>
  );
}

export default PfandEditor;
